package com.htmlagent.site.components.sections.landing

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.htmlagent.site.components.sections.Glyph
import com.htmlagent.site.components.widgets.reveal
import com.htmlagent.site.i18n.LocalStrings
import com.htmlagent.site.theme.FaqAnswerInnerStyle
import com.htmlagent.site.theme.FaqAnswerStyle
import com.htmlagent.site.theme.FaqAnswerTextStyle
import com.htmlagent.site.theme.FaqChevronStyle
import com.htmlagent.site.theme.FaqItemStyle
import com.htmlagent.site.theme.FaqListStyle
import com.htmlagent.site.theme.FaqQuestionStyle
import com.htmlagent.site.theme.FaqRowStyle
import com.htmlagent.site.theme.Icons
import com.htmlagent.site.theme.Tokens
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.forms.Button
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text

/** One thing somebody hesitates over, in their words, and the answer in ours. */
private data class Question(val question: String, val answer: String)

/**
 * The six questions that decide whether somebody asks for a page.
 *
 * It sits after the examples and not before them because an objection arrives once the offer is
 * being believed: nobody asks where they would host a page they have not decided to want.
 *
 * The last one names a competitor and ends by turning work away — a page is not a shop. That
 * sentence is what makes the other five credible, and it is not an oversight. It is also why the
 * three-column grid had to go: in three columns it landed in the bottom right corner.
 *
 * They fold, and more than one can be open at a time. That is the difference between this and the
 * steps next door: those are a sequence and only one can be current, these are six separate
 * things to look up, and closing the one somebody just read to open another would be a filing
 * cabinet that only holds one folder. The first arrives open, which is how anybody learns the
 * rest can be pressed without being told.
 */
@Composable
fun LandingFaq() {
    val t = LocalStrings.current
    val questions = remember(t) {
        listOf(
            Question(t.landing.faqChangeQuestion, t.landing.faqChangeAnswer),
            Question(t.landing.faqHostQuestion, t.landing.faqHostAnswer),
            Question(t.landing.faqFreeQuestion, t.landing.faqFreeAnswer),
            Question(t.landing.faqPhoneQuestion, t.landing.faqPhoneAnswer),
            Question(t.landing.faqCloseQuestion, t.landing.faqCloseAnswer),
            Question(t.landing.faqWixQuestion, t.landing.faqWixAnswer),
        )
    }

    var open by remember { mutableStateOf(setOf(FIRST_OPEN)) }

    Band(SECTION_FAQ) {
        SectionHead(
            t.landing.faqEyebrow,
            t.landing.faqTitle,
            headingId = headingId(SECTION_FAQ),
        )

        Div(FaqListStyle.toModifier().reveal().toAttrs()) {
            questions.forEachIndexed { position, item ->
                FaqRow(
                    item = item,
                    expanded = position in open,
                    onToggle = {
                        open = if (position in open) open - position else open + position
                    },
                )
            }
        }
    }
}

/**
 * One question and the answer folded under it.
 *
 * Its own composable with the toggle as a parameter, which is the shape whose handler actually
 * fires here: written inline in the loop, a real click landed on the question — confirmed with
 * `elementFromPoint` on an enabled button — and the state never moved.
 */
@Composable
private fun FaqRow(item: Question, expanded: Boolean, onToggle: () -> Unit) {
    Button(
        onClick = { onToggle() },
        modifier = FaqItemStyle.toModifier().attrsModifier {
            attr("data-open", expanded.toString())
            attr("aria-expanded", expanded.toString())
        },
        // `variant = null` and one plain style, which is the shape of the menu button in the
        // bar — the one Silk button in this project whose press is proven to arrive.
        variant = null,
    ) {
        Div(FaqRowStyle.toModifier().toAttrs()) {
            H3(FaqQuestionStyle.toModifier().toAttrs()) { Text(item.question) }
            Div(
                FaqChevronStyle.toModifier().toAttrs {
                    attr("data-chevron", "")
                    attr("aria-hidden", "true")
                },
            ) {
                Glyph(Icons.ChevronDown, Tokens.iconSm.value())
            }
        }

        Div(FaqAnswerStyle.toModifier().toAttrs { attr("data-answer", "") }) {
            Div(FaqAnswerInnerStyle.toModifier().toAttrs()) {
                P(FaqAnswerTextStyle.toModifier().toAttrs()) { Text(item.answer) }
            }
        }
    }
}

/** The one that arrives open, so nobody has to be told the others can be pressed. */
private const val FIRST_OPEN = 0
